import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Dashboard from './Dashboard';
import { useAuth } from './hooks/useAuth';
import { authHelpers, profileHelpers } from './lib/supabase';
import LoginModal from './components/LoginModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import EditProfileModal from './components/EditProfileModal';
import ProfileTypeSelector from './components/ProfileTypeSelector';
import OccupationPromptModal from './components/OccupationPromptModal';
import PlaceAutocomplete from './components/PlaceAutocomplete';
import NameAnalysisDashboard from './components/NameAnalysisDashboard';
import MobileAppCTA from './components/MobileAppCTA';
import { useLanguage } from './context/LanguageContext';
import ksLogo from './logo-ks-new.png';
import './i18n';
import './App.css';

const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

const KS_PENDING_QUESTION_KEY = 'ks_pending_question';
const KS_OPEN_ASK_TAB_KEY = 'ks_open_ask_tab';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const PLACE_AUTOCOMPLETE_COUNTRY_CODES = ['in'];

function parseCoordParam(v) {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Mobile (< md): open Shastra “Your Answer” tab when user picks a saved profile. */
function setOpenAskTabIfMobileViewport() {
  try {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      sessionStorage.setItem(KS_OPEN_ASK_TAB_KEY, '1');
    }
  } catch (_) {
    /* ignore */
  }
}

function App() {
  const { t } = useTranslation();
  const { user, loading: authLoading, setOccupationOnUser } = useAuth();
  const { language } = useLanguage();
  const [occPromptDeferred, setOccPromptDeferred] = useState(() => {
    try {
      return sessionStorage.getItem('ks_occupation_defer') === '1';
    } catch {
      return false;
    }
  });
  const location = useLocation();
  
  // Get URL parameters or location state for pre-filling form
  const urlParams = new URLSearchParams(location.search);
  const locationState = location.state || {};
  
  const [formData, setFormData] = useState(() => {
    const bd = locationState.birthData;
    return {
      name: bd?.name || urlParams.get('name') || '',
      email: bd?.email || urlParams.get('email') || '',
      date: bd?.date || urlParams.get('date') || '',
      time: bd?.time || urlParams.get('time') || '',
      place: bd?.place || urlParams.get('place') || '',
      latitude: parseCoordParam(bd?.latitude ?? urlParams.get('lat')),
      longitude: parseCoordParam(bd?.longitude ?? urlParams.get('lng')),
      question: bd?.question || urlParams.get('question') || '',
    };
  });
  const [results, setResults] = useState(locationState.chartData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDashboard, setShowDashboard] = useState(!!locationState.chartData);
  // Login modal
  const [showLoginModal, setShowLoginModal]         = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentProfileId, setCurrentProfileId]     = useState(null);
  const [editingProfile, setEditingProfile]         = useState(null);
  /** When user already has saved kundlis — open modal instead of inline form */
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  /** Add New Member modal: 'complete' (birth details) or 'name_analysis' (name only) */
  const [addMemberProfileMode, setAddMemberProfileMode] = useState('complete');
  /** When true, birth time field is hidden; API & Supabase get 12:00 (noon). */
  const [birthTimeUnknown, setBirthTimeUnknown]   = useState(false);
  // Saved profiles (family members)
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  // Post-signup / no-profile-yet flow: redirects to /onboarding/profile (see
  // needsWelcome below). The flag itself is set by OnboardingProfilePage's
  // "Skip for now" link (sessionStorage) and re-read here on next mount.
  const [welcomeSkipped] = useState(() => {
    try {
      return sessionStorage.getItem('ks_welcome_skipped') === '1';
    } catch {
      return false;
    }
  });
  // A "Name Analysis" saved profile currently being viewed — its own
  // dedicated dashboard with locked upgrade cards instead of a full chart.
  const [nameAnalysisProfile, setNameAnalysisProfile] = useState(null);
  const navigate = useNavigate();

  const normalizeDateForApi = (dateStr) => {
    if (!dateStr || !dateStr.includes('/')) return dateStr;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    const year = parts[2];
    return parseInt(day) > 12 ? `${year}-${month}-${day}` : `${year}-${month}-${day}`;
  };

  /**
   * @param {object} opts
   * @param {boolean} [opts.shouldSave] — persist profile after calculate
   * @param {boolean} [opts.stayOnHome] — after save, stay on home (don’t open dashboard) — for “Add New Member”
   */
  const runCalculation = async (data, opts = {}) => {
    const shouldSave = opts.shouldSave === true;
    const stayOnHome = opts.stayOnHome === true;
    const dateToSend = normalizeDateForApi(data.date);
    const response = await axios.post(`${API_URL}/api/calculate`, {
      ...data,
      date: dateToSend,
      language,
    });
    const chartData = response.data;

    if (!stayOnHome) {
      setResults(chartData);
      setShowDashboard(true);
    } else {
      setResults(null);
      setShowDashboard(false);
    }

    if (shouldSave && data.email && data.name && data.place) {
      try {
        const { data: savedProfile } = await profileHelpers.createProfile({
          email:    data.email,
          name:     data.name,
          dob:      dateToSend,
          tob:      data.time || '',
          place:    data.place,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          question: data.question || '',
          data:     chartData,
        });
        if (savedProfile) {
          setSavedProfiles((prev) => [savedProfile, ...prev.filter((p) => p.id !== savedProfile.id)]);
          setCurrentProfileId(savedProfile.id);
        }
        if (data.question?.trim()) {
          try {
            sessionStorage.removeItem(KS_PENDING_QUESTION_KEY);
          } catch (_) {
            /* ignore */
          }
        }
      } catch (_) {
        /* createProfile errors logged in helper */
      }
    }
  };

  const prevLanguageRef = useRef(language);
  useEffect(() => {
    if (prevLanguageRef.current === language) return;
    prevLanguageRef.current = language;
    if (!showDashboard || !formData.date || !formData.time || !formData.place) return;

    const dateToSend = normalizeDateForApi(formData.date);
    axios
      .post(`${API_URL}/api/calculate`, {
        ...formData,
        date: dateToSend,
        language,
      })
      .then((response) => setResults(response.data))
      .catch((err) => console.error('Language refresh failed:', err));
  }, [language, showDashboard, formData]);

  // Check if location state has chart data (from family member/report click)
  useEffect(() => {
    if (location.state?.chartData) {
      setResults(location.state.chartData);
      setShowDashboard(true);
    }
    // Fresh "Name Analysis" profile just created on /onboarding/profile —
    // open its dashboard immediately instead of waiting on a re-fetch.
    if (location.state?.nameAnalysisProfile) {
      setNameAnalysisProfile(location.state.nameAnalysisProfile);
    }
    const bd = location.state?.birthData;
    if (bd && typeof bd === 'object') {
      setFormData((prev) => ({
        ...prev,
        name: bd.name ?? prev.name,
        date: bd.date ?? prev.date,
        time: bd.time ?? prev.time,
        place: bd.place ?? prev.place,
        latitude: bd.latitude !== undefined ? parseCoordParam(bd.latitude) : prev.latitude,
        longitude: bd.longitude !== undefined ? parseCoordParam(bd.longitude) : prev.longitude,
        question: bd.question != null ? bd.question : prev.question,
        email: bd.email ?? prev.email,
      }));
    }
  }, [location.state]);

  // Pre-login or cross-step: restore typed question (same tab) so dashboard does not re-prompt
  useEffect(() => {
    try {
      const s = (sessionStorage.getItem(KS_PENDING_QUESTION_KEY) || '').trim();
      if (s) {
        setFormData((prev) => (prev.question?.trim() ? prev : { ...prev, question: s }));
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  // Check for extension session in URL params and sync to localStorage
  useEffect(() => {
    const extSession = urlParams.get('ext_session');
    const extEmail = urlParams.get('ext_email');
    
    if (extSession && extEmail && !user) {
      // Set session from extension
      const sessionData = {
        user: {
          email: extEmail,
          id: `ext_${extEmail.replace(/[^a-zA-Z0-9]/g, '_')}`,
          name: extEmail.split('@')[0]
        },
        sessionId: extSession,
        expires: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
      };
      localStorage.setItem('kundli-session', JSON.stringify(sessionData));
      
      // Remove params from URL to clean it up
      const newParams = new URLSearchParams(urlParams);
      newParams.delete('ext_session');
      newParams.delete('ext_email');
      const newUrl = newParams.toString() 
        ? `${window.location.pathname}?${newParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // Reload to trigger auth check
      window.location.reload();
    }
  }, [urlParams, user]);

  // Check if user is logged in and has previous session data
  useEffect(() => {
    if (user && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email || '' }));
    }
    // Do NOT auto-set showDashboard when `results` + formData change — that re-opened the
    // previous kundli whenever e.g. "Add New Member" reset formData. Dashboard opens only via
    // runCalculation, loadProfileKundli, or location.state.chartData.

    // Auto-submit if autoSubmit param is present and form has data
    if (
      urlParams.get('autoSubmit') === 'true' &&
      formData.date &&
      formData.time &&
      formData.place &&
      formData.latitude != null &&
      formData.longitude != null &&
      !results &&
      !loading
    ) {
      const submitEvent = { preventDefault: () => {} };
      handleSubmit(submitEvent);
    }
    
    // If user is logged in but no current calculation, check for URL params
    if (user && !results && (formData.date || formData.name) && !location.state?.chartData) {
      // User has pre-filled data, show a welcome message
      if (process.env.NODE_ENV === 'development') {
      console.log('Welcome back! Form pre-filled for user:', user.email);
      }
    }
  }, [user, results, formData, location.state]);

  // Load saved profiles when user is logged in
  useEffect(() => {
    if (user?.email) {
      setLoadingProfiles(true);
      profileHelpers.getProfiles(user.email).then(({ data }) => {
        setSavedProfiles(data || []);
        setLoadingProfiles(false);
      }).catch(() => setLoadingProfiles(false));
    }
  }, [user]);

  // After password signup, backend may stash chart + profile in session — open dashboard once.
  useEffect(() => {
    if (!user) return;
    try {
      const s = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      const p = s.pendingDashboard;
      if (p?.chartData?.planets) {
        setResults(p.chartData);
        let mergedQ = p.birthData?.question != null ? p.birthData.question : '';
        if (!String(mergedQ || '').trim()) {
          try {
            mergedQ = sessionStorage.getItem(KS_PENDING_QUESTION_KEY) || '';
          } catch (_) {
            /* ignore */
          }
        }
        setFormData((prev) => ({
          ...prev,
          name: p.birthData?.name ?? prev.name,
          date: p.birthData?.date ?? prev.date,
          time: p.birthData?.time ?? prev.time,
          place: p.birthData?.place ?? prev.place,
          latitude:
            p.birthData?.latitude !== undefined
              ? parseCoordParam(p.birthData.latitude)
              : prev.latitude,
          longitude:
            p.birthData?.longitude !== undefined
              ? parseCoordParam(p.birthData.longitude)
              : prev.longitude,
          question: mergedQ || prev.question,
          email: p.birthData?.email || user.email || prev.email,
        }));
        setCurrentProfileId(p.profileId ?? null);
        setShowDashboard(true);
        delete s.pendingDashboard;
        localStorage.setItem('kundli-session', JSON.stringify(s));
      }
    } catch (_) {
      /* ignore */
    }
  }, [user]);

  const refreshProfiles = () => {
    if (user?.email) {
      profileHelpers.getProfiles(user.email).then(({ data }) => setSavedProfiles(data || []));
    }
  };

  const loadProfileKundli = async (profile) => {
    // Name Analysis profiles have no real birth data yet — show the dedicated
    // Name Analysis dashboard instead of calling /api/calculate, which would
    // fail (or worse, "succeed" against placeholder birth data).
    if (profile?.data?.profileType === 'name_analysis') {
      setNameAnalysisProfile(profile);
      return;
    }
    setCurrentProfileId(profile.id || null);
    let pendingQ = '';
    try {
      pendingQ = (sessionStorage.getItem(KS_PENDING_QUESTION_KEY) || '').trim();
    } catch (_) {
      /* ignore */
    }
    const fromProfile = (profile.question && String(profile.question).trim()) || '';
    // Sync formData so Dashboard's birthData (date/time/place) is always correct
    setFormData(prev => ({
      ...prev,
      name:  profile.name  || prev.name,
      date:  profile.dob   || prev.date,
      time:  profile.tob   || prev.time,
      place: profile.place || prev.place,
      latitude: profile.latitude !== undefined && profile.latitude !== null && profile.latitude !== ''
        ? parseCoordParam(profile.latitude)
        : prev.latitude,
      longitude: profile.longitude !== undefined && profile.longitude !== null && profile.longitude !== ''
        ? parseCoordParam(profile.longitude)
        : prev.longitude,
      question: fromProfile || pendingQ || '',
    }));
    // If profile already has stored chart data, use it directly — no API call needed
    if (profile.data && profile.data.planets) {
      setResults(profile.data);
      setShowDashboard(true);
      setOpenAskTabIfMobileViewport();
      return;
    }
    // Otherwise recalculate — shouldSave=false, we already have this profile
    setLoading(true);
    setError(null);
    const plat = parseCoordParam(profile.latitude);
    const plng = parseCoordParam(profile.longitude);
    const pData = {
      name: profile.name,
      email: user?.email,
      date: profile.dob,
      time: profile.tob,
      place: profile.place,
      question: profile.question || '',
      ...(plat != null && plng != null ? { latitude: plat, longitude: plng } : {}),
    };
    try {
      await runCalculation(pData, { shouldSave: false });
      setOpenAskTabIfMobileViewport();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load kundli');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceChange = useCallback(({ placeOfBirth, latitude, longitude }) => {
    setFormData((prev) => ({
      ...prev,
      place: placeOfBirth,
      latitude,
      longitude,
    }));
    setError(null);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'question' && typeof value === 'string') {
      try {
        if (value.trim()) {
          sessionStorage.setItem(KS_PENDING_QUESTION_KEY, value);
        } else {
          sessionStorage.removeItem(KS_PENDING_QUESTION_KEY);
        }
      } catch (_) {
        /* ignore */
      }
    }
    setError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);

    // Add New Member → Name Analysis mode: no birth details, no question —
    // create via the independent /api/name-analysis service (same helper the
    // onboarding page uses) and stay on the home page.
    if (showAddMemberModal && addMemberProfileMode === 'name_analysis') {
      if (!formData.name?.trim()) {
        setError('Please enter full name');
        return;
      }
      setLoading(true);
      try {
        const { error: createErr } = await profileHelpers.createNameAnalysisProfile({
          name: formData.name.trim(),
          email: user.email,
        });
        if (createErr) throw new Error(createErr.message || 'Could not save this profile');

        setShowAddMemberModal(false);
        setAddMemberProfileMode('complete');
        setBirthTimeUnknown(false);
        setFormData({
          name: '',
          email: user?.email || '',
          date: '',
          time: '',
          place: '',
          latitude: null,
          longitude: null,
          question: '',
        });
        refreshProfiles();
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to save name analysis profile');
      } finally {
        setLoading(false);
      }
      return;
    }

    const effectiveTime = birthTimeUnknown ? '12:00' : (formData.time || '').trim();
    if (!formData.name?.trim()) {
      setError('Please enter full name');
      return;
    }
    if (!formData.date || !formData.place?.trim()) {
      setError('Please fill in birth date and place');
      return;
    }
    if (formData.latitude == null || formData.longitude == null) {
      setError('Please select a valid location from suggestions');
      return;
    }
    if (!birthTimeUnknown && !effectiveTime) {
      setError('Please enter birth time, or check “I don’t know my birth time”');
      return;
    }

    const questionTrim = (formData.question != null ? String(formData.question) : '').trim();
    if (questionTrim.length < 10) {
      setError(
        'Please enter your question (at least 10 characters) so we can personalise your chart — / ' +
          'Kripya apna sawaal likhein (kam se kam 10 characters).'
      );
      return;
    }

    const stayOnHome = !!(user && savedProfiles.length > 0 && showAddMemberModal);

    // Always use the logged-in user's email — no OTP flow needed
    setLoading(true);
    setResults(null);
    try {
      await runCalculation(
        { ...formData, time: effectiveTime, email: user.email, question: questionTrim },
        { shouldSave: true, stayOnHome }
      );
      if (stayOnHome) {
        setShowAddMemberModal(false);
        setBirthTimeUnknown(false);
        setFormData({
          name: '',
          email: user?.email || '',
          date: '',
          time: '',
          place: '',
          latitude: null,
          longitude: null,
          question: '',
        });
        refreshProfiles();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to calculate astrology data');
    } finally {
      setLoading(false);
    }
  };

  const showOccPrompt =
    !!user &&
    (user.occupation == null || !String(user.occupation).trim()) &&
    !occPromptDeferred;

  // Loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen app-shell-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-saffron-200 border-t-saffron-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-saffron-600 font-medium">Loading your cosmic insights...</p>
        </div>
      </div>
    );
  }

  // Show dashboard if results exist and dashboard is toggled
  if (showDashboard && results) {
    return (
      <>
        {showOccPrompt && (
          <OccupationPromptModal
            onSave={setOccupationOnUser}
            onDefer={() => {
              try {
                sessionStorage.setItem('ks_occupation_defer', '1');
              } catch (_) {
                /* ignore */
              }
              setOccPromptDeferred(true);
            }}
          />
        )}
        <Dashboard
          data={results}
          onBack={() => {
            setShowDashboard(false);
            setResults(null);
          }}
          onNavigateToAddMember={() => {
            setShowDashboard(false);
            setResults(null);
            setBirthTimeUnknown(false);
            setFormData({
              name: '',
              email: user?.email || '',
              date: '',
              time: '',
              place: '',
              latitude: null,
              longitude: null,
              question: '',
            });
            setError(null);
            setAddMemberProfileMode('complete');
            setShowAddMemberModal(true);
          }}
          birthData={formData}
          onProfileSaved={refreshProfiles}
          profileId={currentProfileId}
          userId={user?.id}
        />
      </>
    );
  }

  // ── Show inline login/signup when NOT logged in ──────────────────────────────
  if (!user) {
    const reviews = [
      { name: 'Priya Sharma', city: 'Mumbai', stars: 5, text: 'Bilkul sahi bata diya — meri job change ke baare mein jo prediction thi, woh 2 mahine mein sach ho gayi. Incredible accuracy!', avatar: 'PS' },
      { name: 'Rahul Verma', city: 'Delhi', stars: 5, text: 'Maine sochaa tha ye sab timepass hai, par jab meri kundli mein meri exact problem mention thi toh main hairan reh gaya. Must try!', avatar: 'RV' },
      { name: 'Anita Joshi', city: 'Pune', stars: 5, text: 'My question about marriage timing was answered so precisely. The PDF report was detailed and easy to understand. Worth every rupee.', avatar: 'AJ' },
      { name: 'Suresh Nair', city: 'Bangalore', stars: 5, text: 'I\'ve tried many astrology apps but this one feels genuinely personalised. The Dasha predictions matched real events in my life.', avatar: 'SN' },
      { name: 'Meena Gupta', city: 'Jaipur', stars: 5, text: 'Mera business question tha — kab expand karein? Report ne exact time window batayi. Ab main confidently plan kar rahi hoon.', avatar: 'MG' },
      { name: 'Vikram Singh', city: 'Lucknow', stars: 5, text: 'The karmic insights section was eye-opening. Never thought astrology could explain so much about my repeated life patterns.', avatar: 'VS' },
      { name: 'Kavita Reddy', city: 'Hyderabad', stars: 5, text: 'Ghar parivaar aur career dono ke baare mein jo likha tha, lagbhag waise hi hua. Simple language mein samajh aa gaya — parents ke liye bhi perfect.', avatar: 'KR' },
      { name: 'Deepak Khanna', city: 'Chandigarh', stars: 5, text: 'Pehli baar online kundli try ki — Gochar timing ne help ki shares bechne mein. Support team ne Hindi mein guide bhi kiya. Dil se thanks!', avatar: 'DK' },
    ];

    const avatarColors = ['#b45309','#92400e','#78350f','#a16207','#854d0e','#713f12','#9a3412','#57534e'];

    /* Narrower card so login + signup columns share equal width; fields align visually */
    const guestContentWidth = 'min(960px, 100%)';

    return (
      <div
        className="min-h-screen app-shell-bg"
        style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'2rem clamp(0.75rem, 3vw, 2rem) 3rem' }}
      >
        {/* Brand header */}
        <div style={{ textAlign:'center', marginBottom:'1.5rem', width: guestContentWidth }}>
          <img src={ksLogo} alt="Kundli Shastra" style={{ height:'64px', width:'auto', marginBottom:'0.25rem', objectFit:'contain', display:'block', margin:'0 auto 0.25rem' }} />
          <h1 style={{ fontSize:'clamp(1.5rem, 4vw, 2rem)', fontWeight:800, color:'#78350f', margin:0 }}>Kundli Shastra</h1>
          <p style={{ color:'#b45309', fontSize:'clamp(0.82rem, 2.2vw, 0.95rem)', margin:'0.3rem 0 0', lineHeight:1.45, fontWeight:600 }}>
            Personalised Kundli Analysis · {t('app.subtitle')}
          </p>
        </div>

        {/* Inline login/signup panel */}
        <div style={{ width: guestContentWidth, borderRadius:'16px', overflow:'hidden', boxShadow:'0 8px 30px rgba(0,0,0,0.06), 0 16px 48px rgba(0,0,0,0.08)', background:'#ffffff', border:'1px solid rgb(254 215 170 / 0.45)' }}>
          <LoginModal
            isOpen={true}
            onClose={() => {}}
            onSuccess={() => {}}
            inline={true}
          />
        </div>

        {/* ── Trust Stats Bar ── */}
        <div
          className="guest-stats-bar"
          style={{
            width: guestContentWidth,
            marginTop:'2.5rem',
            display:'grid',
            gridTemplateColumns:'repeat(3, minmax(0, 1fr))',
            gap:'0',
            background:'linear-gradient(135deg,#78350f,#b45309)',
            borderRadius:'1rem',
            overflow:'hidden',
            boxShadow:'0 8px 32px rgba(120,53,15,0.3)',
          }}
        >
          {[
            { num: '12,000+', label: 'Kundlis Generated', labelHi: 'Kundli banayi gayi' },
            { num: '4.9 ⭐', label: 'Average Rating', labelHi: 'Average rating' },
            { num: '99%', label: 'Accuracy Reported', labelHi: 'Accuracy reported' },
          ].map((stat, i) => (
            <div key={i} className="guest-stats-bar__cell" style={{ padding:'clamp(0.85rem, 2vw, 1.25rem) clamp(0.65rem, 2vw, 1rem)', textAlign:'center' }}>
              <div style={{ fontSize:'clamp(1.15rem, 3.5vw, 1.6rem)', fontWeight:800, color:'#fef3c7', letterSpacing:'-0.5px' }}>{stat.num}</div>
              <div style={{ fontSize:'clamp(0.68rem, 2vw, 0.75rem)', color:'rgba(255,255,255,0.75)', marginTop:'0.2rem', fontWeight:500, lineHeight:1.35 }}>
                <span style={{ display:'block' }}>{stat.labelHi}</span>
                <span style={{ display:'block', fontSize:'0.9em', opacity:0.92 }}>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Reviews Section ── */}
        <div style={{ width: guestContentWidth, marginTop:'2rem' }}>
          <div style={{ textAlign:'center', marginBottom:'1.25rem' }}>
            <h2 style={{ fontSize:'1.2rem', fontWeight:700, color:'#78350f', margin:0 }}>What our users say</h2>
            <p style={{ fontSize:'0.8rem', color:'#92400e', margin:'0.35rem 0 0', lineHeight:1.45 }}>Real people. Real insights. Real results.</p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap:'0.85rem' }}>
            {reviews.map((r, i) => (
              <div key={i} style={{ background:'#fff', borderRadius:'14px', padding:'1.1rem', boxShadow:'0 4px 20px rgba(120,53,15,0.08)', border:'1px solid #fde68a', display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                {/* Stars */}
                <div style={{ color:'#f59e0b', fontSize:'0.85rem', letterSpacing:'1px' }}>{'★'.repeat(r.stars)}</div>
                {/* Review text */}
                <p style={{ margin:0, fontSize:'0.8rem', color:'#44403c', lineHeight:1.55, flexGrow:1 }}>"{r.text}"</p>
                {/* Author */}
                <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginTop:'0.25rem' }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:avatarColors[i % avatarColors.length], display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, color:'#fff', flexShrink:0 }}>
                    {r.avatar}
                  </div>
                  <div>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#78350f' }}>{r.name}</div>
                    <div style={{ fontSize:'0.7rem', color:'#a16207' }}>{r.city}</div>
                  </div>
                  <div style={{ marginLeft:'auto', fontSize:'0.62rem', background:'#fef3c7', color:'#92400e', padding:'0.15rem 0.5rem', borderRadius:'999px', fontWeight:600 }}>Verified</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Bottom Trust Badges ── */}
        <div style={{ width: guestContentWidth, marginTop:'1.75rem', display:'flex', justifyContent:'center', gap:'clamp(0.75rem, 2vw, 1.5rem)', flexWrap:'wrap' }}>
          {[
            { icon:'🔒', text: 'Secure & Private' },
            { icon:'🧮', text: 'Vedic calculations' },
            { icon:'⚡', text: 'Instant results' },
          ].map((b, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'clamp(0.7rem, 2vw, 0.78rem)', color:'#78350f', fontWeight:600, textAlign:'center' }}>
              <span style={{ fontSize:'1rem' }}>{b.icon}</span> {b.text}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── New user, no saved profile yet → send to the dedicated onboarding page ──
  // (Phase 2 of the profile-creation refactor.) Keeps auth and Kundli-profile
  // creation as separate steps; redirects once until the user creates a
  // profile (savedProfiles.length becomes > 0) or explicitly skips it.
  const needsWelcome =
    !!user &&
    !loadingProfiles &&
    savedProfiles.length === 0 &&
    !results &&
    !showDashboard &&
    !welcomeSkipped &&
    !nameAnalysisProfile;

  if (needsWelcome) {
    return <Navigate to="/onboarding/profile" replace />;
  }

  // ── Name Analysis profile selected — its own dedicated dashboard ──────────
  if (nameAnalysisProfile) {
    return (
      <NameAnalysisDashboard
        profile={nameAnalysisProfile}
        onBack={() => setNameAnalysisProfile(null)}
        onCompleteProfile={() => navigate(`/onboarding/profile?upgrade=${nameAnalysisProfile.id}`)}
      />
    );
  }

  return (
    <div className="min-h-screen app-shell-bg">
      {showOccPrompt && (
        <OccupationPromptModal
          onSave={setOccupationOnUser}
          onDefer={() => {
            try {
              sessionStorage.setItem('ks_occupation_defer', '1');
            } catch (_) {
              /* ignore */
            }
            setOccPromptDeferred(true);
          }}
        />
      )}

      {/* Login Modal (for when logged-in user wants to switch accounts — not normally visible) */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
      />
      {user && (
        <div style={{ background:'linear-gradient(90deg,#78350f,#92400e)', padding:'0.55rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', flexWrap:'wrap' }}>
          <span style={{ color:'rgba(255,255,255,.85)', fontSize:'0.82rem' }}>🙏 Welcome back, <strong>{user.name || user.email}</strong></span>
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
            <button
              onClick={() => setShowChangePassword(true)}
              title={user.hasPassword ? 'Change your password' : 'Set a password for your account'}
              style={{ background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'0.3rem 0.8rem', borderRadius:'999px', fontSize:'0.75rem', cursor:'pointer' }}
            >
              🔑 {user.hasPassword ? 'Change Password' : 'Set Password'}
            </button>
            <button
              onClick={async () => { await authHelpers.signOut(); window.location.reload(); }}
              style={{ background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'0.3rem 0.9rem', borderRadius:'999px', fontSize:'0.75rem', cursor:'pointer' }}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userEmail={user?.email}
        hasPassword={!!user?.hasPassword}
      />
      <EditProfileModal
        profile={editingProfile}
        userEmail={user?.email}
        onClose={() => setEditingProfile(null)}
        onSaved={(updated) => {
          setSavedProfiles(prev => prev.map(p => p.id === updated.id ? updated : p));
          setEditingProfile(null);
        }}
      />

      {/* Add New Member — same fields as main form; stays on home after save */}
      {showAddMemberModal && user && (
        <>
          <div
            role="presentation"
            onClick={() => { if (!loading) { setShowAddMemberModal(false); setError(null); } }}
            className="ks-modal-scrim"
            style={{ position:'fixed', inset:0, zIndex:10050 }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-member-title"
            style={{
              position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:10051,
              width:'min(480px,94vw)', maxHeight:'min(92vh,720px)', overflow:'auto', background:'#ffffff',
              borderRadius:'16px', boxShadow:'0 8px 30px rgba(0,0,0,0.06), 0 24px 60px rgba(0,0,0,0.12)', border:'1px solid rgb(254 215 170 / 0.45)', fontFamily:'system-ui,sans-serif',
            }}
          >
            <div style={{ background:'linear-gradient(135deg,#78350f,#b45309)', padding:'1rem 1.25rem', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:1 }}>
              <span id="add-member-title" style={{ color:'#fff', fontWeight:800, fontSize:'1rem' }}>➕ Add New Member</span>
              <button
                type="button"
                onClick={() => { if (!loading) { setShowAddMemberModal(false); setError(null); } }}
                style={{ background:'rgba(255,255,255,.2)', border:'none', color:'#fff', width:28, height:28, borderRadius:'50%', cursor:loading?'not-allowed':'pointer', fontSize:17 }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding:'1.5rem' }}>
              {error && (
                <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:'0.5rem', padding:'0.6rem 0.8rem', marginBottom:'0.85rem', fontSize:'0.8rem', color:'#dc2626' }}>{error}</div>
              )}

              <ProfileTypeSelector value={addMemberProfileMode} onChange={setAddMemberProfileMode} compact />

              {[
                { label: t('app.fullName', 'Full Name'), name: 'name', type: 'text', ph: t('app.namePlaceholder', 'Enter your full name') },
                ...(addMemberProfileMode === 'name_analysis' ? [] : [{ label: t('app.birthDate'), name: 'date', type: 'date', ph: '' }]),
              ].map((f) => (
                <div key={f.name} style={{ marginBottom:'0.85rem' }}>
                  <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, color:'#92400e', marginBottom:'0.25rem' }}>{f.label}</label>
                  <input
                    type={f.type}
                    name={f.name}
                    value={formData[f.name]}
                    onChange={handleChange}
                    placeholder={f.ph}
                    required={f.name === 'date'}
                    min={f.name === 'date' ? '1900-01-01' : undefined}
                    max={f.name === 'date' ? new Date().toISOString().split('T')[0] : undefined}
                    style={{ width:'100%', padding:'0.6rem 0.8rem', borderRadius:'0.5rem', border:'1.5px solid #fcd34d', outline:'none', fontSize:'0.85rem', color:'#78350f', background:'#fffbeb', boxSizing:'border-box' }}
                  />
                </div>
              ))}

              {addMemberProfileMode !== 'name_analysis' && (
                <>
                  <div style={{ marginBottom:'0.85rem' }}>
                    <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, color:'#92400e', marginBottom:'0.25rem' }}>{t('app.birthTime')}</label>
                    {!birthTimeUnknown && (
                      <input
                        type="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        style={{ width:'100%', padding:'0.6rem 0.8rem', borderRadius:'0.5rem', border:'1.5px solid #fcd34d', outline:'none', fontSize:'0.85rem', color:'#78350f', background:'#fffbeb', boxSizing:'border-box' }}
                      />
                    )}
                    {birthTimeUnknown && (
                      <p style={{ fontSize:'0.8rem', color:'#92400e', background:'#fff7ed', border:'1px solid #fde68a', borderRadius:'0.5rem', padding:'0.6rem 0.75rem', margin:0 }}>
                        Using <strong>12:00 PM (noon)</strong> for calculations.
                      </p>
                    )}
                    <label style={{ marginTop:'0.65rem', display:'flex', alignItems:'flex-start', gap:'0.5rem', cursor:'pointer', fontSize:'0.78rem', color:'#78350f' }}>
                      <input
                        type="checkbox"
                        checked={birthTimeUnknown}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setBirthTimeUnknown(checked);
                          setFormData((prev) => ({ ...prev, time: checked ? '12:00' : '' }));
                          setError(null);
                        }}
                        style={{ marginTop:3 }}
                      />
                      <span>
                        {t('app.birthTimeUnknown', "I don't know my birth time — use 12:00 PM (noon)")}
                        <span style={{ display:'block', fontSize:'0.68rem', color:'#a16207', marginTop:4 }}>
                          Janam samay exact na ho toh yeh default use hota hai; baad mein profile se update kar sakte hain.
                        </span>
                      </span>
                    </label>
                  </div>
                  <div style={{ marginBottom:'0.85rem' }}>
                    <PlaceAutocomplete
                      id="place-add-member"
                      apiKey={GOOGLE_MAPS_API_KEY}
                      label={t('app.birthPlace')}
                      placeOfBirth={formData.place}
                      latitude={formData.latitude}
                      longitude={formData.longitude}
                      onPlaceChange={handlePlaceChange}
                      countryCodes={PLACE_AUTOCOMPLETE_COUNTRY_CODES}
                    />
                  </div>
                  <div style={{ background:'linear-gradient(135deg,#fff7ed,#fef3c7)', borderRadius:'0.75rem', padding:'0.9rem', border:'1.5px solid #f59e0b', marginBottom:'1rem' }}>
                    <label htmlFor="add-member-question" style={{ display:'block', fontSize:'0.8rem', fontWeight:700, color:'#92400e', marginBottom:'0.45rem' }}>
                      🔮 Apna ek prashn likhein <span style={{ color:'#dc2626' }}>*</span>{' '}
                      <span style={{ fontWeight:400, color:'#b45309' }}>(kam se kam 10 characters)</span>
                    </label>
                    <textarea
                      id="add-member-question"
                      name="question"
                      value={formData.question}
                      onChange={handleChange}
                      rows={3}
                      required
                      minLength={10}
                      placeholder="e.g., 'Career mein ab kya karna chahiye?', 'Vivah kab hoga?', 'Business sahi rahega?'"
                      style={{ width:'100%', padding:'0.65rem 0.85rem', borderRadius:'0.5rem', border:'1.5px solid #fcd34d', outline:'none', fontSize:'0.85rem', color:'#78350f', background:'#fffbeb', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}
                    />
                    <p style={{ fontSize:'0.68rem', color:'#b45309', margin:'0.4rem 0 0' }}>
                      ✨ Aapke iss prashn ka answer aapke premium report mein personally explain kiya jayega
                    </p>
                  </div>
                </>
              )}

              {addMemberProfileMode === 'name_analysis' && (
                <p style={{ fontSize:'0.75rem', color:'#a16207', lineHeight:1.55, margin:'0 0 1rem' }}>
                  You can always add birth details later to unlock their complete Kundli.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width:'100%', padding:'0.75rem', background:'linear-gradient(135deg,#d97706,#b45309)', color:'#fff', border:'none',
                  borderRadius:'0.6rem', fontWeight:700, fontSize:'0.9rem', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1,
                }}
              >
                {loading
                  ? t('app.calculating')
                  : addMemberProfileMode === 'name_analysis'
                    ? '✨ Generate Name Analysis'
                    : '💾 Add member & save kundli'}
              </button>
              <p style={{ fontSize:'0.7rem', color:'#b45309', textAlign:'center', marginTop:'0.65rem' }}>
                {addMemberProfileMode === 'name_analysis'
                  ? 'Their Name Analysis will be saved to your saved kundlis'
                  : 'Their chart will be calculated and saved to your saved kundlis'}
              </p>
            </form>
          </div>
        </>
      )}

      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-6xl md:text-7xl font-bold text-saffron-700 mb-4 leading-tight">
              {t('app.title')}
            </h1>
            <p className="text-xl md:text-2xl text-saffron-600 mb-2 font-medium">
              {t('app.subtitle')}
            </p>
            <p className="text-lg text-saffron-500 max-w-3xl mx-auto mb-6">
              {t('app.heroDescription', 'Get your Vedic astrology birth chart (Kundli) with planetary positions, divisional charts, astrological insights, and numerology. Instant and precise calculations.')}
            </p>
            <div className="mt-6 w-32 h-1.5 bg-gradient-to-r from-saffron-400 to-saffron-600 mx-auto rounded-full"></div>
          </div>

          {/* Profile-completion nudge — shown when the user skipped profile creation */}
          {user && savedProfiles.length === 0 && welcomeSkipped && (
            <div className="max-w-2xl mx-auto mb-8">
              <div style={{ background:'linear-gradient(135deg,#fff7ed,#fef3c7)', borderRadius:'1.25rem', padding:'1.25rem 1.5rem', border:'2px solid #f59e0b', display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap', justifyContent:'space-between' }}>
                <p style={{ margin:0, fontSize:'0.88rem', color:'#78350f', fontWeight:600, lineHeight:1.5, flex:'1 1 260px' }}>
                  ✨ Complete your profile to unlock personalised predictions, Mahadasha, Numerology &amp; Karmic Insights.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/onboarding/profile')}
                  style={{ flexShrink:0, padding:'0.6rem 1.2rem', borderRadius:'0.6rem', border:'none', background:'linear-gradient(135deg,#d97706,#b45309)', color:'#fff', fontWeight:700, fontSize:'0.85rem', cursor:'pointer' }}
                >
                  Create My Profile
                </button>
              </div>
            </div>
          )}

          {/* Mobile app promo */}
          {user && (
            <div className="max-w-2xl mx-auto mb-8">
              <MobileAppCTA />
            </div>
          )}

          {/* Saved Kundlis — shown prominently when logged in with data */}
          {user && savedProfiles.length > 0 && (
            <div className="max-w-2xl mx-auto mb-8">
              <div style={{ background:'rgba(255,255,255,0.95)', borderRadius:'1.5rem', padding:'1.5rem', border:'2px solid #fed7aa', boxShadow:'0 4px 24px rgba(180,83,9,.1)' }}>
                <h3 style={{ fontSize:'1.05rem', fontWeight:700, color:'#92400e', marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  🪐 Your Saved Kundlis
                </h3>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'0.75rem' }}>
                  {savedProfiles.slice(0, 6).map(p => {
                    const isNameAnalysis = p?.data?.profileType === 'name_analysis';
                    return (
                    <div key={p.id} style={{ position:'relative' }}>
                      <button
                        onClick={() => loadProfileKundli(p)}
                        disabled={loading}
                        style={{ width:'100%', background:'linear-gradient(135deg,#fff7ed,#fef3c7)', border:'1.5px solid #fcd34d', borderRadius:'1rem', padding:'0.85rem 0.75rem', fontSize:'0.82rem', fontWeight:600, color:'#92400e', cursor:'pointer', textAlign:'center', transition:'all .2s', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.35rem' }}
                      >
                        <span style={{ fontSize:'1.5rem' }}>👤</span>
                        <span style={{ wordBreak:'break-word' }}>{p.name || 'Profile'}</span>
                        {isNameAnalysis
                          ? <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#b45309', background:'#fef3c7', borderRadius:'999px', padding:'0.1rem 0.5rem' }}>Name Analysis</span>
                          : (p.place && <span style={{ fontSize:'0.7rem', fontWeight:400, color:'#b45309' }}>{p.place}</span>)}
                      </button>
                      {/* Edit pencil */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isNameAnalysis) navigate(`/onboarding/profile?upgrade=${p.id}`);
                          else setEditingProfile(p);
                        }}
                        title="Edit details"
                        style={{ position:'absolute', top:6, right:6, background:'rgba(255,255,255,0.85)', border:'1px solid #fcd34d', borderRadius:'50%', width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'0.7rem', lineHeight:1, boxShadow:'0 1px 4px rgba(0,0,0,.1)' }}
                      >
                        ✏️
                      </button>
                    </div>
                  );})}
                </div>
                <p style={{ fontSize:'0.72rem', color:'#b45309', margin:'0.85rem 0 0', textAlign:'center' }}>
                  Tap any name to view · ✏️ to edit details
                </p>
              </div>
            </div>
          )}

          {/* Main Form Card — full page only until user has saved kundlis; then use “Add New Member” modal */}
          <div className="max-w-2xl mx-auto mb-16">
            {user && savedProfiles.length > 0 && (
              <div style={{ textAlign:'center', marginBottom:'0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setBirthTimeUnknown(false);
                    setFormData({
                      name: '',
                      email: user?.email || '',
                      date: '',
                      time: '',
                      place: '',
                      latitude: null,
                      longitude: null,
                      question: '',
                    });
                    setError(null);
                    setShowDashboard(false);
                    setResults(null);
                    setAddMemberProfileMode('complete');
                    setShowAddMemberModal(true);
                  }}
                  style={{ background:'transparent', border:'2px solid #f59e0b', color:'#92400e', padding:'0.5rem 1.5rem', borderRadius:'999px', fontWeight:600, fontSize:'0.85rem', cursor:'pointer' }}
                >
                  ➕ Add New Member
                </button>
              </div>
            )}
            <div style={{ display: user && savedProfiles.length > 0 ? 'none' : 'block' }}>
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-10 border-2 border-saffron-200">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-saffron-700 mb-2">
                  {t('app.calculateYourKundli', 'Calculate Your Kundli')}
                </h2>
                <p className="text-saffron-600">
                  {t('app.enterBirthDetails', 'Enter your birth details to get started')}
                </p>
              </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-saffron-700 mb-2">
                  {t('app.fullName', 'Full Name')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('app.namePlaceholder', 'Enter your full name')}
                  className="w-full px-4 py-3 rounded-lg border-2 border-saffron-200 focus:border-saffron-400 focus:outline-none transition-colors text-saffron-900 placeholder-saffron-300"
                />
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-medium text-saffron-700 mb-2">
                  {t('app.birthDate')}
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min="1900-01-01"
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 rounded-lg border-2 border-saffron-200 focus:border-saffron-400 focus:outline-none transition-colors text-saffron-900"
                />
              </div>

              <div>
                <label htmlFor="time" className="block text-sm font-medium text-saffron-700 mb-2">
                  {t('app.birthTime')}
                </label>
                {!birthTimeUnknown && (
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-saffron-200 focus:border-saffron-400 focus:outline-none transition-colors text-saffron-900"
                  />
                )}
                {birthTimeUnknown && (
                  <p className="text-sm text-saffron-600 bg-saffron-50/80 border border-saffron-200 rounded-lg px-4 py-3">
                    Using <strong>12:00 PM (noon)</strong> for calculations — chart accuracy is best with exact time.
                  </p>
                )}
                <label className="mt-3 flex items-start gap-2.5 cursor-pointer text-sm text-saffron-800">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-saffron-300 text-saffron-600 focus:ring-saffron-500"
                    checked={birthTimeUnknown}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setBirthTimeUnknown(checked);
                      setFormData((prev) => ({ ...prev, time: checked ? '12:00' : '' }));
                      setError(null);
                    }}
                  />
                  <span>
                    {t('app.birthTimeUnknown', "I don't know my birth time — use 12:00 PM (noon)")}
                    <span className="block text-xs text-saffron-600 font-normal mt-0.5">
                      Janam samay exact na ho toh yeh default use hota hai; baad mein profile se update kar sakte hain.
                    </span>
                  </span>
                </label>
              </div>

              <div>
                <PlaceAutocomplete
                  id="place"
                  apiKey={GOOGLE_MAPS_API_KEY}
                  label={t('app.birthPlace')}
                  placeOfBirth={formData.place}
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  onPlaceChange={handlePlaceChange}
                  countryCodes={PLACE_AUTOCOMPLETE_COUNTRY_CODES}
                />
              </div>

              {/* Question field — key for conversion */}
              <div style={{ background:'linear-gradient(135deg,#fff7ed,#fef3c7)', borderRadius:'0.75rem', padding:'1rem', border:'1.5px solid #f59e0b' }}>
                <label htmlFor="question" style={{ display:'block', fontSize:'0.875rem', fontWeight:700, color:'#92400e', marginBottom:'0.5rem' }}>
                  🔮 Apna ek prashn likhein <span style={{ color:'#dc2626' }}>*</span>{' '}
                  <span style={{ fontWeight:400, color:'#b45309' }}>(kam se kam 10 characters — zaroori)</span>
                </label>
                <textarea
                  id="question"
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  rows={2}
                  required
                  minLength={10}
                  placeholder="e.g., 'Career mein ab kya karna chahiye?', 'Vivah kab hoga?', 'Business sahi rahega?'"
                  style={{ width:'100%', padding:'0.65rem 0.85rem', borderRadius:'0.5rem', border:'1.5px solid #fcd34d', outline:'none', fontSize:'0.875rem', color:'#78350f', background:'#fffbeb', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}
                />
                <p style={{ fontSize:'0.72rem', color:'#b45309', margin:'0.4rem 0 0' }}>
                  ✨ Aapke iss prashn ka answer aapke premium report mein personally explain kiya jayega
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-saffron-500 to-saffron-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-saffron-600 hover:to-saffron-700 focus:outline-none focus:ring-4 focus:ring-saffron-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {loading ? t('app.calculating') : t('app.calculate')}
              </button>
            </form>
            </div>
          </div>
          </div> {/* end new-kundli-form wrapper */}

          {/* Error Message (inline form — modal shows its own) */}
          {error && !showAddMemberModal && (
            <div className="max-w-2xl mx-auto mb-6">
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

        </div>
      </section>


    </div>
  );
}

export default App;

